import { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Tabs,
    Tab,
    TextField,
    Typography,
    Grid2 as Grid,
    Switch,
    FormControlLabel,
    Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { t } from 'i18next';
import { CommonCard } from '../../component/cards/MainCard2';
import { SubmitButton } from '../../component/buttons';
import { patchClinic } from '../../services/clinics';
import ManagementHeader from './componets/ManagementHeader';

const ClinicSettings = () => {
    const { state } = useLocation();
    const { t } = useTranslation();
    const clinic = state?.clinic;

    const navigate = useNavigate();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [dialogError, setDialogError] = useState(false);

    const DEFAULT_FEATURES = [
        { key: 'REHAB', label: t('clinics.featuresNames.rehab') },
        { key: 'TRAIN', label: t('clinics.featuresNames.trainingPlans') },
        { key: 'NUTRITION', label: t('clinics.featuresNames.nutritionPlans') },
        { key: 'QUESTIONNAIRES', label: t('clinics.featuresNames.questionnaires') },
        { key: 'CHAT', label: t('clinics.featuresNames.chat') },
        { key: 'LEARNING', label: t('clinics.featuresNames.learning') },
        { key: 'TELECONSULT', label: t('clinics.featuresNames.teleconsultations') }
    ];

    const [tab, setTab] = useState(0);

    const [form, setForm] = useState({
        name: clinic?.name || '',
        email: clinic?.email || '',
        plan: clinic?.plan || '',
        addr_text: clinic?.address?.street || '',
        addr_zip: clinic?.address?.zip || '',
        addr_city: clinic?.address?.city || '',
        addr_province: clinic?.address?.province || '',
        addr_country: clinic?.address?.country || '',
        features: clinic?.features || [],
        logo_url: clinic?.picture || '',
        logo_file: null
    });

    const handleChange = (field) => (e) => {
        setForm((prev) => ({
            ...prev,
            [field]: e.target.value
        }));
    };

    const toggleFeature = (feature) => {
        setForm((prev) => ({
            ...prev,
            features: prev.features.includes(feature)
                ? prev.features.filter((f) => f !== feature)
                : [...prev.features, feature]
        }));
    };

    const handleLogoUpload = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setForm((prev) => ({
            ...prev,
            logo_file: file,
            logo_url: URL.createObjectURL(file)
        }));
    };

    const renderGeneralTab = () => (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    label={t('clinics.name')}
                    fullWidth
                    value={form.name}
                    onChange={handleChange('name')}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    label={t('clinics.email')}
                    fullWidth
                    value={form.email}
                    onChange={handleChange('email')}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                    label={t('clinics.plan')}
                    fullWidth
                    value={form.plan}
                    disabled
                />
            </Grid>
        </Grid>
    );

    const renderAddressTab = () => (
        <Grid container spacing={2}>
            <Grid size={12}>
                <TextField
                    label={t('clinics.address')}
                    fullWidth
                    value={form.addr_text}
                    onChange={handleChange('addr_text')}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    label={t('clinics.zip')}
                    fullWidth
                    value={form.addr_zip}
                    onChange={handleChange('addr_zip')}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    label={t('clinics.city')}
                    fullWidth
                    value={form.addr_city}
                    onChange={handleChange('addr_city')}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    label={t('clinics.province')}
                    fullWidth
                    value={form.addr_province}
                    onChange={handleChange('addr_province')}
                />
            </Grid>

            <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                    label={t('clinics.country')}
                    fullWidth
                    value={form.addr_country}
                    onChange={handleChange('addr_country')}
                />
            </Grid>
        </Grid>
    );

    const renderFeaturesTab = () => (
        <Box>
            <Typography variant="subtitle1" gutterBottom>
                {t('clinics.enabledFeatures')}
            </Typography>

            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
                {DEFAULT_FEATURES.map((feature) => (
                    <Grid size={{ xs: 12, md: 6 }} key={feature.key}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.features.includes(feature.key)}
                                    onChange={() => toggleFeature(feature.key)}
                                />
                            }
                            label={feature.label}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    const renderBrandingTab = () => (
        <Grid container spacing={3}>
            {/* Preview */}
            <Grid size={12}>
                <Typography variant="subtitle2" gutterBottom>
                    {t('clinics.logoPreview')}
                </Typography>

                <Box
                    sx={{
                        width: 200,
                        height: 200,
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fff'
                    }}
                >
                    {form.logo_url ? (
                        <img
                            src={form.logo_url}
                            alt="Clinic logo"
                            style={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain'
                            }}
                        />
                    ) : (
                        <Typography variant="caption" color="text.secondary">
                            {t('clinics.noLogo')}
                        </Typography>
                    )}
                </Box>
            </Grid>

            {/* Upload button */}
            <Grid size={12}>
                <SubmitButton component="label">
                    {t('clinics.uploadLogo')}
                    <input
                        type="file"
                        hidden
                        accept="image/png, image/jpeg, image/svg+xml"
                        onChange={handleLogoUpload}
                    />
                </SubmitButton>
            </Grid>
        </Grid>
    );

    const handleSubmit = async () => {
        try {
            const payload = {
                uuid: clinic.uuid,
                name: form.name,
                email: form.email,
                plan: form.plan,
                features: form.features,
                street: form.addr_text,
                zip: form.addr_zip,
                city: form.addr_city,
                province: form.addr_province,
                country: form.addr_country
            };

            await patchClinic(payload);

            setDialogMessage(
                t('clinics.updateSuccess') || 'Clinic updated successfully.'
            );
            setDialogError(false);
            setDialogOpen(true);
        } catch (err) {
            console.error(err);

            setDialogMessage(
                err?.response?.data?.description ||
                    t('clinics.updateError') ||
                    'Failed to update clinic.'
            );
            setDialogError(true);
            setDialogOpen(true);
        }
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
        navigate('/mgmt/clinics');
    };

    return (
        <CommonCard sx={{ m: 2, p: 2 }}>
            {/* Header */}
            <ManagementHeader
                title={t('clinics.settings')}
                subtitle={form.name}
                backTo="/mgmt/clinics"
            />

            <Card>
                <Tabs
                    value={tab}
                    onChange={(_, v) => setTab(v)}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label={t('clinics.tabs.general')} />
                    <Tab label={t('clinics.tabs.address')} />
                    <Tab label={t('clinics.tabs.features')} />
                    <Tab label={t('clinics.tabs.branding')} />
                </Tabs>

                <CardContent sx={{ backgroundColor: '#f9f9f9' }}>
                    {tab === 0 && renderGeneralTab()}
                    {tab === 1 && renderAddressTab()}
                    {tab === 2 && renderFeaturesTab()}
                    {tab === 3 && renderBrandingTab()}
                </CardContent>
            </Card>
            <Grid
                size={12}
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    m: 2
                }}
            >
                <SubmitButton onClick={handleSubmit}>{t('common.save')}</SubmitButton>
            </Grid>
            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogContent>
                    <Typography>{dialogMessage}</Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleDialogClose} variant="contained">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </CommonCard>
    );
};

export default ClinicSettings;
